'use client';

import LandingLayout from '@/components/LandingLayout';
import { StatusHero } from '@/components/StatusHero';
import { trackPageView } from '@/lib/analytics';
import {
    ArrowRightIcon,
    BuildingStorefrontIcon,
    WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useEffect } from 'react';

interface IndustryCard {
  name: string;
  slug: string;
  description: string;
  painPoint: string;
  stat: string;
  statLabel: string;
  image: string;
  hasLandingPage: boolean;
  blogSlug?: string;
}

const industries: IndustryCard[] = [
  {
    name: 'Plumbing',
    slug: '/plumbing',
    description: 'Answer emergency calls 24/7, schedule repairs, and pre-qualify jobs while you\'re on-site.',
    painPoint: 'Miss emergency calls while on jobs',
    stat: '$500-2K',
    statLabel: 'per missed emergency call',
    image: '/images/blog/plumbing.jpg',
    hasLandingPage: true,
    blogSlug: 'plumber-business-growth',
  },
  {
    name: 'Salons & Spas',
    slug: '/salon',
    description: 'Book appointments 24/7, reduce no-shows, and handle scheduling changes automatically.',
    painPoint: 'Can\'t answer while with clients',
    stat: '35%',
    statLabel: 'no-show reduction',
    image: '/images/blog/salon.jpg',
    hasLandingPage: true,
    blogSlug: 'salon-ai-receptionist',
  },
  {
    name: 'Restaurants',
    slug: '/restaurants',
    description: 'Handle reservation calls, answer menu questions, and manage catering inquiries hands-free.',
    painPoint: 'Phones ring during dinner rush',
    stat: '40%',
    statLabel: 'of calls during peak hours',
    image: '/images/blog/restaurant.jpg',
    hasLandingPage: true,
    blogSlug: 'restaurant-ai-receptionist',
  },
  {
    name: 'Landscaping',
    slug: '/landscapers',
    description: 'Capture seasonal leads, book estimates, and handle FAQs while your crew is in the field.',
    painPoint: 'Lose leads during busy season',
    stat: '60%',
    statLabel: 'of calls missed in peak season',
    image: '/images/blog/landscaping.jpg',
    hasLandingPage: true,
    blogSlug: 'landscaping-ai-receptionist',
  },
  {
    name: 'Locksmith',
    slug: '/locksmith',
    description: '24/7 emergency dispatch, instant response to lockout calls, and service area confirmation.',
    painPoint: '60%+ of calls are after hours',
    stat: '24/7',
    statLabel: 'emergency dispatch coverage',
    image: '/images/blog/locksmith.jpg',
    hasLandingPage: true,
    blogSlug: 'locksmith-ai-receptionist',
  },
  {
    name: 'Hardscape Contractors',
    slug: '/hardscape-contractors',
    description: 'Qualify project leads, schedule estimates, and answer material and pricing questions.',
    painPoint: 'On job sites, can\'t take calls',
    stat: '$2-10K',
    statLabel: 'average project value at risk',
    image: '/images/blog/laying-brick.jpg',
    hasLandingPage: true,
    blogSlug: 'hardscape-competitive-advantage',
  },
  {
    name: 'Electricians',
    slug: '/blog/electrician-business-growth',
    description: 'Capture emergency calls, schedule inspections, and handle permit questions professionally.',
    painPoint: 'Hands in a panel, can\'t answer',
    stat: '$200-800',
    statLabel: 'per missed service call',
    image: '/images/blog/electrician.jpg',
    hasLandingPage: false,
    blogSlug: 'electrician-business-growth',
  },
  {
    name: 'HVAC',
    slug: '/blog/hvac-ai-receptionist',
    description: 'Handle seasonal surges, schedule installations, and triage AC/heating emergencies.',
    painPoint: 'Summer/winter call volume spikes',
    stat: '3x',
    statLabel: 'call volume in peak seasons',
    image: '/images/blog/hvac.jpg',
    hasLandingPage: false,
    blogSlug: 'hvac-ai-receptionist',
  },
  {
    name: 'Dental Practices',
    slug: '/blog/dental-ai-receptionist',
    description: 'Schedule patient appointments, handle insurance questions, and reduce no-shows.',
    painPoint: 'Can\'t answer during procedures',
    stat: '20%',
    statLabel: 'no-show reduction',
    image: '/images/blog/dentist.jpg',
    hasLandingPage: false,
    blogSlug: 'dental-ai-receptionist',
  },
  {
    name: 'Pet Grooming',
    slug: '/blog/pet-grooming-ai-receptionist',
    description: 'Book grooming appointments, answer breed-specific questions, and handle seasonal rushes.',
    painPoint: 'Hands full with furry clients',
    stat: '50+',
    statLabel: 'calls/day during holidays',
    image: '/images/blog/pet-grooming.jpg',
    hasLandingPage: false,
    blogSlug: 'pet-grooming-ai-receptionist',
  },
  {
    name: 'Painting Contractors',
    slug: '/blog/painting-contractor-ai-receptionist',
    description: 'Capture estimate requests, schedule walkthroughs, and answer coverage questions.',
    painPoint: 'On ladders, can\'t reach phone',
    stat: '$1-5K',
    statLabel: 'average job value at risk',
    image: '/images/blog/painter.jpg',
    hasLandingPage: false,
    blogSlug: 'painting-contractor-ai-receptionist',
  },
  {
    name: 'Auto Repair Shops',
    slug: '/blog/auto-repair-ai-receptionist',
    description: 'Schedule service appointments, provide repair estimates, and handle after-hours towing calls.',
    painPoint: 'Under the hood, can\'t answer',
    stat: '$200-500',
    statLabel: 'per missed service appointment',
    image: '/images/blog/hvac-installer.jpg',
    hasLandingPage: false,
    blogSlug: 'auto-repair-ai-receptionist',
  },
  {
    name: 'Cleaning Companies',
    slug: '/blog/cleaning-company-ai-receptionist',
    description: 'Quote recurring services, book one-time cleans, and capture high-value commercial leads.',
    painPoint: 'At client sites all day',
    stat: '$10K+',
    statLabel: 'annual value per recurring client',
    image: '/images/blog/landscaping.jpg',
    hasLandingPage: false,
    blogSlug: 'cleaning-company-ai-receptionist',
  },
  {
    name: 'Veterinary Clinics',
    slug: '/blog/veterinary-clinic-ai-receptionist',
    description: 'Triage emergency calls, schedule multi-provider appointments, and answer pet care questions.',
    painPoint: 'Emergencies can\'t wait for voicemail',
    stat: '24/7',
    statLabel: 'emergency triage coverage',
    image: '/images/blog/pet-grooming.jpg',
    hasLandingPage: false,
    blogSlug: 'veterinary-clinic-ai-receptionist',
  },
  {
    name: 'Outdoor Contractors',
    slug: '/blog/outdoor-contractors-seasonal-rush',
    description: 'Manage seasonal demand, schedule installations, and handle weather-related reschedules.',
    painPoint: 'Seasonal rushes overwhelm phones',
    stat: '4x',
    statLabel: 'call volume spring/summer',
    image: '/images/blog/sod.jpg',
    hasLandingPage: false,
    blogSlug: 'outdoor-contractors-seasonal-rush',
  },
];

export default function IndustriesPage() {
  useEffect(() => {
    trackPageView('Industries Hub', undefined, { funnel_step: 'industry_hub' });
  }, []);

  const landingPageIndustries = industries.filter(i => i.hasLandingPage);
  const blogIndustries = industries.filter(i => !i.hasLandingPage);

  return (
    <LandingLayout>
      <StatusHero
        icon={BuildingStorefrontIcon}
        title="Industry Solutions"
        description="Greet's AI phone receptionist is tailored for your industry. See how businesses like yours answer every call, book more appointments, and grow revenue."
      />

      {/* Featured Industries with Landing Pages */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Industries</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Dedicated AI receptionist solutions built for the unique needs of your business.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {landingPageIndustries.map((industry) => (
            <Link
              key={industry.name}
              href={industry.slug}
              className="group block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1"
            >
              <div className="relative">
                <img
                  src={industry.image}
                  alt={`AI phone receptionist for ${industry.name}`}
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {industry.name}
                </h3>
                <p className="text-gray-600 mb-4 text-sm">{industry.description}</p>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mb-4">
                  <p className="text-sm text-amber-800">
                    <span className="font-semibold">Pain point:</span> {industry.painPoint}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-blue-600">{industry.stat}</span>
                    <span className="text-xs text-gray-500 ml-1">{industry.statLabel}</span>
                  </div>
                  <span className="inline-flex items-center text-blue-600 font-medium text-sm group-hover:translate-x-1 transition-transform">
                    Learn More <ArrowRightIcon className="h-4 w-4 ml-1" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Answering service alternatives */}
      <div className="bg-white border-y border-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Comparing Answering Services?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Side-by-side guides for people searching for traditional answering services vs an AI receptionist.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { href: '/answering-service-for-plumbers', label: 'Answering Service for Plumbers' },
              { href: '/salon-answering-service', label: 'Salon Answering Service' },
              { href: '/landscaping-answering-service', label: 'Landscaping Answering Service' },
              { href: '/ai-receptionist-for-coffee-shops', label: 'AI Receptionist for Coffee Shops' },
              { href: '/ai-receptionist-pricing', label: 'AI Receptionist Pricing' },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-5 py-4 hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                <span className="font-semibold text-gray-900">{item.label}</span>
                <ArrowRightIcon className="h-4 w-4 text-blue-600 shrink-0 ml-3" />
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* More Industries */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">More Industries We Serve</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From electricians to veterinary clinics, small businesses across dozens of industries trust Greet to handle their calls.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogIndustries.map((industry) => (
              <Link
                key={industry.name}
                href={industry.slug}
                className="group flex items-start gap-4 bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all hover:border-blue-200"
              >
                <div className="shrink-0">
                  <div className="h-12 w-12 rounded-lg bg-blue-50 flex items-center justify-center">
                    <WrenchScrewdriverIcon className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                    {industry.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">{industry.description}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-blue-600">{industry.stat}</span>
                    <span className="text-xs text-gray-500">{industry.statLabel}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Common Benefits Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Small Businesses Choose Greet</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center p-6">
            <div className="text-4xl font-bold text-blue-600 mb-2">100%</div>
            <div className="text-lg font-semibold text-gray-900 mb-2">Calls Answered</div>
            <p className="text-gray-600">Never miss a customer call again, even at 2 AM on a Sunday. Greet answers every call instantly.</p>
          </div>
          <div className="text-center p-6">
            <div className="text-4xl font-bold text-blue-600 mb-2">24/7</div>
            <div className="text-lg font-semibold text-gray-900 mb-2">Availability</div>
            <p className="text-gray-600">Your AI receptionist works nights, weekends, and holidays. No overtime, no sick days, no coverage gaps.</p>
          </div>
          <div className="text-center p-6">
            <div className="text-4xl font-bold text-blue-600 mb-2">5 min</div>
            <div className="text-lg font-semibold text-gray-900 mb-2">Setup Time</div>
            <p className="text-gray-600">Enter your business details, connect your calendar, and go live. No technical expertise required.</p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mb-16">
        <div className="bg-blue-600 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Don&apos;t See Your Industry?
          </h3>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Greet works for any service-based business that takes phone calls. Try it free and see how AI phone answering transforms your customer communication.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/try"
              className="inline-block bg-white text-blue-600 hover:bg-gray-50 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              Try It Free — No Signup
            </Link>
            <Link
              href="/register"
              className="inline-block bg-blue-500 text-white hover:bg-blue-400 px-8 py-4 rounded-lg font-semibold text-lg transition-colors border border-blue-400"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </div>
    </LandingLayout>
  );
}
